/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useState } from "react";

/**
 * VoiceView: the rail title while it should be spoken — from the moment
 * the rail becomes active (focus entered it, or it mounted active) until the
 * focused tile moves within it. So "On Now, The Oscars, …" on entry, then
 * just "Red Queen, …" on the next tile.
 *
 * Uses the adjust-state-during-render pattern so the answer is already
 * correct in the render that moves DOM focus, with no extra effect pass.
 *
 * @param isActive - Whether this rail holds D-pad focus.
 * @param focusedIndex - The rail's focused tile index.
 * @param title - The rail title.
 * @param isEnabled - Whether the rail syncs accessibility focus at all.
 * @returns The title to speak before the focused tile, or undefined.
 */
export function useRailEntryAnnouncement(
  isActive: boolean,
  focusedIndex: number,
  title: string,
  isEnabled: boolean
): string | undefined {
  const [prevIsActive, setPrevIsActive] = useState<boolean>(isActive);
  const [prevFocusedIndex, setPrevFocusedIndex] =
    useState<number>(focusedIndex);
  const [isEntry, setIsEntry] = useState<boolean>(isActive);

  if (prevIsActive !== isActive) {
    setPrevIsActive(isActive);
    setPrevFocusedIndex(focusedIndex);
    setIsEntry(isActive);
  } else if (prevFocusedIndex !== focusedIndex) {
    setPrevFocusedIndex(focusedIndex);
    setIsEntry(false);
  }

  return isEnabled && isActive && isEntry ? title : undefined;
}
